import React, { useCallback, useEffect, useState, useMemo } from 'react';
import * as localization from '@happylife-a/localization';
import s3Storage from '@happylife-a/s3-storage';
import utils from '@happylife-a/utils';
import webCore from '@happylife-a/web-core';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
  IconButton,
  Text,
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Center,
  Spinner,
  Image,
} from '@chakra-ui/react';
import TextField from '../TextField';
import { theme } from '../../theme';

function parseToDecimal(value) {
  if (value % 1 === 0 || value.toString().endsWith('.')) {
    return value;
  }

  const decimals = value.toString().split('.')[1].length;
  return Number(value).toFixed(decimals > 2 ? 2 : decimals);
}

export default function DiscountedPriceModal({ productId, onSave, onClose }) {
  const { t, locale } = webCore.contexts.useTranslation();

  const [isUpdating, setIsUpdating] = useState(false);
  const [percentages, setPercentages] = useState({});
  const [discountedPrices, setDiscountedPrices] = useState({});
  const [updatedIds, setUpdatedIds] = useState([]);

  const { data: product, isLoading } = webCore.coreHooks.product.useGetProduct(productId);

  const { mutateAsync: updateDetail } = webCore.coreHooks.product.useUpdateProductDetail(productId);

  useEffect(() => {
    if (!product) {
      return;
    }

    setPercentages(
      product.details.reduce(
        (acc, detail) => ({
          ...acc,
          [detail.id]: detail.discountPercentage || '',
        }),
        {}
      )
    );

    setDiscountedPrices(
      product.details.reduce((acc, detail) => ({ ...acc, [detail.id]: detail.discountedPrice }), {})
    );
  }, [product]);

  const onSaveClick = useCallback(async () => {
    setIsUpdating(true);

    const promises = [];
    for (const updatedId of updatedIds) {
      promises.push(
        updateDetail({
          id: updatedId,
          discountPercentage: percentages[updatedId],
          discountedPrice: discountedPrices[updatedId],
        })
      );
    }

    await Promise.all(promises);
    setIsUpdating(false);

    if (typeof onSave === 'function') {
      onSave();
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [updatedIds, percentages, discountedPrices]);

  const handleValuesChange = useCallback(
    ({ newPercentageValue, newDiscountedPriceValue, detailId }) => {
      let shouldCalculate = true;
      let percentageValueToSet = '';
      let discountedPriceValueToSet = '';

      if (newPercentageValue === '' || newDiscountedPriceValue === '') {
        shouldCalculate = false;
      }

      if (shouldCalculate) {
        const { price: originalPrice } = product.details.find((detail) =>
          utils.helpers.id.same(detail.id, detailId)
        );

        if (typeof newPercentageValue === 'string') {
          if (newPercentageValue > 100 || newPercentageValue < 0) {
            return;
          }
          percentageValueToSet = parseToDecimal(newPercentageValue);

          discountedPriceValueToSet = parseToDecimal(
            originalPrice - (newPercentageValue / 100) * originalPrice
          );
        } else if (typeof newDiscountedPriceValue === 'string') {
          if (newDiscountedPriceValue > originalPrice || newDiscountedPriceValue < 0) {
            return;
          }
          discountedPriceValueToSet = parseToDecimal(newDiscountedPriceValue);

          percentageValueToSet = parseToDecimal(
            ((originalPrice - newDiscountedPriceValue) / originalPrice) * 100
          );
        }
      }

      setPercentages((oldPercentages) => ({
        ...oldPercentages,
        [detailId]: percentageValueToSet,
      }));

      setDiscountedPrices((oldDiscountedPrices) => ({
        ...oldDiscountedPrices,
        [detailId]: discountedPriceValueToSet,
      }));

      setUpdatedIds((oldUpdatedIds) => [...new Set([...oldUpdatedIds, detailId])]);
    },
    [product]
  );

  const formatPercentage = (value) => {
    if (!value) {
      return '';
    }
    return `% ${value}`;
  };

  const productVariants = useMemo(
    () =>
      product?.details?.length === 0
        ? []
        : product?.details[0].values
            .filter((value) => {
              return !!value.productVariantValueId;
            })
            .map((val) => ({
              id: val.field.id,
              label: localization.localize(val.field.label, locale),
            })),
    [product, locale]
  );

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="90vw" width="auto">
        <ModalBody px={5}>
          <Flex position="relative" justifyContent="space-between" alignItems="center">
            <Text variant="sub-head-semi">{t('AppDiscountedPrice')}</Text>
            <IconButton
              variant="transparent"
              minW="unset"
              icon={<theme.icons.CloseCircle fill="none" w={5} h={5} />}
              onClick={onClose}
            />
          </Flex>

          {isLoading ? (
            <Center minH={40}>
              <Spinner />
            </Center>
          ) : (
            <>
              <Box overflowX="auto">
                <Table border="unset" overflowX="scroll" whiteSpace="nowrap" width="auto">
                  <Thead>
                    <Tr>
                      <Td border="unset">
                        <Text variant="body-reg-lg" color="grey.900">
                          {t('AppProductName')}
                        </Text>
                      </Td>
                      {productVariants.map((field) => (
                        <Td key={`ProductDiscountModal-value-${field.id}`} border="unset">
                          <Text variant="body-reg-lg" color="grey.900">
                            {field.label}
                          </Text>
                        </Td>
                      ))}
                      <Td border="unset">
                        <Text variant="body-reg-lg" color="grey.900">
                          {t('Price')}
                        </Text>
                      </Td>
                      <Td border="unset">
                        <Text variant="body-reg-lg" color="grey.900">
                          {t('AppSale')} {'%'}
                        </Text>
                      </Td>
                      <Td border="unset">
                        <Text variant="body-reg-lg" color="grey.900">
                          {t('AppDiscountedPrice')}
                        </Text>
                      </Td>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {product.details.map((detail, index) => (
                      <Tr key={`ProductDiscountModal-detail-${detail.id}-${index}`}>
                        <Td border="unset">
                          <Flex alignItems="center">
                            <Box borderColor="grey.300" borderWidth={1} borderRadius="8px">
                              <Image
                                src={s3Storage.helpers.read.buildUrlProduct(
                                  detail.images.length > 0
                                    ? detail.images[0].imageKey
                                    : product.images[0].imageKey
                                )}
                                alt=""
                                borderRadius="10px"
                                width="36px"
                                height="36px"
                              />
                            </Box>
                            <Text variant="body-reg-lg" color="grey.900" ml={3}>
                              {localization.localize(product.title, locale)}
                            </Text>
                          </Flex>
                        </Td>
                        {productVariants.map((field) => {
                          const value = detail.values.find((val) =>
                            utils.helpers.id.same(val.field.id, field.id)
                          );
                          return (
                            <Td
                              key={`ProductDiscountModal-detail-${detail.id}-value-${value.id}`}
                              border="unset"
                            >
                              <Text variant="body-reg-lg" color="grey.900">
                                {utils.helpers.product.formFieldValueByLocale(
                                  value,
                                  locale,
                                  'field'
                                )}
                              </Text>
                            </Td>
                          );
                        })}
                        <Td border="unset">
                          <Box bg="grey.50" py={3} px={6} borderRadius={8} textAlign="center">
                            <Text variant="body-reg-lg" color="grey.900">
                              {utils.helpers.currency.beautify(detail.price)}
                            </Text>
                          </Box>
                        </Td>
                        <Td border="unset">
                          <TextField
                            variant="variant2"
                            max={100}
                            min={0}
                            value={formatPercentage(percentages[detail.id])}
                            onChange={(value) =>
                              handleValuesChange({
                                detailId: detail.id,
                                newPercentageValue: value,
                              })
                            }
                          />
                        </Td>
                        <Td border="unset">
                          <TextField
                            variant="variant2"
                            max={detail.price}
                            min={0}
                            value={
                              discountedPrices[detail.id]
                                ? `${utils.helpers.currency.getCharacter()} ${discountedPrices[detail.id]}`
                                : ''
                            }
                            onChange={(value) =>
                              handleValuesChange({
                                detailId: detail.id,
                                newDiscountedPriceValue: value,
                              })
                            }
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Flex
                position="relative"
                justifyContent="center"
                alignItems="center"
                mt={4}
                gap={4}
                mb={3}
              >
                <Button
                  variant="filledBig"
                  width="280px"
                  height="36px"
                  borderRadius="12px"
                  isLoading={isUpdating}
                  onClick={onSaveClick}
                >
                  {t('AppSave')}
                </Button>
              </Flex>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
